import type { ModuleIntroConfig } from "@/components/onboarding/configs/getModuleConfig";
import type { OnboardingModule, OnboardingProgress } from "@/types/onboarding";

import { twMerge } from "tailwind-merge";

type OnboardingChecklistItemProps = {
  module: OnboardingModule;
  progress?: OnboardingProgress;
  intro?: ModuleIntroConfig;
  /**
   * Total walkthrough steps for this module, derived from the frontend tour
   * config (the BE does not return it). Drives the "X/N" progress count.
   */
  totalSteps?: number;
  index: number; // 1-based position shown in the badge
  onStart: (code: string, intro?: ModuleIntroConfig) => void;
};

export const OnboardingChecklistItem = ({
  module,
  progress,
  intro,
  totalSteps = 0,
  index,
  onStart
}: OnboardingChecklistItemProps) => {
  const currentStep = progress?.current_step ?? 0;
  const isInProgress = progress?.status === "in_progress";
  const isCompleted = progress?.status === "completed";
  // Intro always precedes the first step: show it whenever the module sits at
  // step 0 (fresh start). Resumes past step 0 skip the intro.
  const shouldShowIntro = Boolean(intro) && currentStep === 0;

  const handleClick = () => {
    if (shouldShowIntro) {
      onStart(module.code, intro);
      return;
    }

    onStart(module.code);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={twMerge(
        "flex w-full items-center gap-3 rounded-xl p-2 text-left hover:bg-neutral-100"
      )}
    >
      <span
        className={twMerge(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold  border-teal-500",
          isCompleted
            ? "bg-teal-300 text-white"
            : isInProgress
              ? "border-2 bg-green-50  text-txtHeading"
              : "bg-neutral-100 text-dim-gray"
        )}
      >
        {index}
      </span>
      <span className="flex flex-1 flex-col">
        <span
          className={twMerge(
            "text-sm",
            isCompleted
              ? "font-bold text-teal-600"
              : isInProgress
                ? "font-bold text-txtHeading"
                : "font-semibold text-txtHeading"
          )}
        >
          {module.title}
        </span>
        <span
          className={twMerge(
            "text-xs text-dim-gray",
            isCompleted && "text-teal-500"
          )}
        >
          {module.description}
        </span>
      </span>
      {totalSteps > 0 && (
        <span className="shrink-0 text-sm text-dim-gray">
          {currentStep}/{totalSteps}
        </span>
      )}
    </button>
  );
};

export default OnboardingChecklistItem;
